import { Helmet } from "react-helmet-async";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/i18n/LanguageContext";
import { threatMapSeo } from "@/i18n/seo";
import { LANG_HTML, LOCALE, SITE_URL, localizedPath } from "@/lib/routes";

const ThreatMap = () => {
  const { lang } = useLanguage();
  const navigate = useNavigate();
  const seoTitle = threatMapSeo.title[lang];
  const seoDescription = threatMapSeo.description[lang];
  const h1 = threatMapSeo.h1[lang];
  const support = threatMapSeo.support[lang];
  const canonical = `${SITE_URL}${localizedPath("threatMap", lang)}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    name: seoTitle,
    description: seoDescription,
    url: canonical,
    inLanguage: LANG_HTML[lang],
    isPartOf: { "@type": "WebSite", name: "SentinelHub", url: SITE_URL },
  };

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const data = event.data;
      if (data?.type === "sentinelhub:go-contact") {
        navigate(`${localizedPath("home", lang)}#contact`);
        return;
      }
      if (data?.type === "sentinelhub:change-lang" && data.lang) {
        const target = data.lang as "pt" | "en" | "es";
        if (target === lang) return;
        navigate(localizedPath("threatMap", target));
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [lang, navigate]);

  return (
    <div className="fixed inset-0 bg-black">
      <Helmet>
        <html lang={LANG_HTML[lang]} />
        <title>{seoTitle}</title>
        <meta name="description" content={seoDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={seoTitle} />
        <meta property="og:description" content={seoDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content={LOCALE[lang]} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Equivalente textual do canvas para leitores de tela e buscadores */}
      <h1 className="sr-only">{h1}</h1>
      <p className="sr-only">{support}</p>

      <iframe
        src={`/threat-map-embed.html?lang=${lang}`}
        title={seoTitle}
        className="absolute inset-0 h-full w-full border-0"
        allow="fullscreen"
      />
    </div>
  );
};

export default ThreatMap;
